import React, { useState } from 'react';
import { Mail, Phone, Github, Linkedin, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { portfolioData } from '../mock';
import { useReveal } from '../hooks/useReveal';

const Contact = () => {
  const [ref, visible] = useReveal();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill all fields', {
        description: 'All fields are required to send your message.',
      });
      return;
    }

    const subject = `Portfolio · ${formData.subject}`;
    const body = `Hi Tharak,

Name: ${formData.name}
Email: ${formData.email}
Subject: ${formData.subject}

${formData.message}

(sent from your portfolio contact form)`;

    const mailto = `mailto:${portfolioData.personal.email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;

    toast.success('Opening your email client...', {
      description: 'Your message is pre-filled and ready to send.',
    });

    setTimeout(() => {
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 1000);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-28 sm:py-36 px-5 sm:px-8 lg:px-12 border-t border-[hsl(var(--rule))] bg-[hsl(var(--surface))]"
    >
      <div ref={ref} className={`max-w-6xl mx-auto reveal ${visible ? 'is-visible' : ''}`}>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 space-y-10">
            <div>
              <div className="section-label mb-6">
                <span>06 / say hi</span>
              </div>
              <h2 className="display text-4xl sm:text-6xl leading-[0.95]">
                Let's build
                <br />
                something
                <br />
                <span className="italic text-[hsl(var(--accent))]">together.</span>
              </h2>
            </div>

            <p className="text-xl text-[hsl(var(--foreground-muted))] leading-relaxed max-w-md">
              Got a project, a research question, or just want to say hello? My inbox is always open.
            </p>

            <div className="space-y-5 pt-4">
              <a
                data-testid="contact-email-link"
                href={`mailto:${portfolioData.personal.email}`}
                className="flex items-center gap-3 group"
              >
                <Mail className="w-4 h-4 text-[hsl(var(--accent))]" />
                <span className="mono text-sm link-underline">
                  {portfolioData.personal.email}
                </span>
              </a>
              <a
                data-testid="contact-phone-link"
                href={`tel:${portfolioData.personal.phone.replace(/[^\d+]/g, '')}`}
                className="flex items-center gap-3 group"
              >
                <Phone className="w-4 h-4 text-[hsl(var(--accent))]" />
                <span className="mono text-sm link-underline">
                  {portfolioData.personal.phone}
                </span>
              </a>
            </div>

            <div className="flex items-center gap-5 pt-2">
              <a
                data-testid="contact-github"
                href={portfolioData.personal.github}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 border border-[hsl(var(--rule))] rounded-full hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-all"
                aria-label="GitHub"
              >
                <Github className="w-4 h-4" />
              </a>
              <a
                data-testid="contact-linkedin"
                href={portfolioData.personal.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 border border-[hsl(var(--rule))] rounded-full hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-all"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>

            <div className="flex items-center gap-3 pt-3 mono text-xs text-[hsl(var(--foreground-faint))]">
              <CheckCircle2 className="w-4 h-4 text-[hsl(var(--accent))]" />
              <span>open to research roles, ML/AI positions, and collaborations</span>
            </div>
          </div>

          {/* Form */}
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            noValidate
            className="lg:col-span-7 space-y-6 p-6 sm:p-10 border border-[hsl(var(--rule))] rounded-sm bg-[hsl(var(--background))]"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="mono text-[0.7rem] uppercase tracking-wider text-[hsl(var(--foreground-faint))] block mb-2">
                  name *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  data-testid="contact-name-input"
                  value={formData.name}
                  onChange={onChange}
                  required
                  className="font-sans-ui w-full bg-transparent border-b border-[hsl(var(--rule))] py-2.5 text-base text-[hsl(var(--foreground))] placeholder-[hsl(var(--foreground-faint))] focus:border-[hsl(var(--accent))] focus:outline-none transition-colors"
                  placeholder="your full name"
                />
              </div>
              <div>
                <label htmlFor="email" className="mono text-[0.7rem] uppercase tracking-wider text-[hsl(var(--foreground-faint))] block mb-2">
                  email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="contact-email-input"
                  value={formData.email}
                  onChange={onChange}
                  required
                  className="font-sans-ui w-full bg-transparent border-b border-[hsl(var(--rule))] py-2.5 text-base text-[hsl(var(--foreground))] placeholder-[hsl(var(--foreground-faint))] focus:border-[hsl(var(--accent))] focus:outline-none transition-colors"
                  placeholder="you@domain.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="subject" className="mono text-[0.7rem] uppercase tracking-wider text-[hsl(var(--foreground-faint))] block mb-2">
                subject *
              </label>
              <input
                id="subject"
                name="subject"
                type="text"
                data-testid="contact-subject-input"
                value={formData.subject}
                onChange={onChange}
                required
                className="font-sans-ui w-full bg-transparent border-b border-[hsl(var(--rule))] py-2.5 text-base text-[hsl(var(--foreground))] placeholder-[hsl(var(--foreground-faint))] focus:border-[hsl(var(--accent))] focus:outline-none transition-colors"
                placeholder="what's this about?"
              />
            </div>

            <div>
              <label htmlFor="message" className="mono text-[0.7rem] uppercase tracking-wider text-[hsl(var(--foreground-faint))] block mb-2">
                message *
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                data-testid="contact-message-input"
                value={formData.message}
                onChange={onChange}
                required
                className="font-sans-ui w-full bg-transparent border-b border-[hsl(var(--rule))] py-2.5 text-base text-[hsl(var(--foreground))] placeholder-[hsl(var(--foreground-faint))] focus:border-[hsl(var(--accent))] focus:outline-none transition-colors resize-none"
                placeholder="tell me about your idea, project, or just say hello"
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit-button"
              className="btn-mono btn-mono-accent mt-4"
            >
              send message <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
